import { Component, Input, OnInit, ChangeDetectionStrategy, input, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Kendo UI imports
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { SwitchModule } from '@progress/kendo-angular-inputs';

// Import the TenantMappingComponent
import { TenantMappingComponent } from '../../tenant-mapping/tenant-mapping.component';
import {
  ElasticSyncStatus,
  formatIngestorApiError,
  IngestorRecordDetail,
  IngestorService,
} from 'src/app/features/admin/organizations/components/integrations/services/ingestor.service';
import { ConnectWiseService } from 'src/app/core/services/connectwise.service';
import { BaseComponent } from 'src/app/core/shared/common/base.component';


@Component({
  selector: 'app-configured-ingestion-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, IconsModule, InputsModule, LabelModule, DropDownsModule, LoaderModule, TabStripModule, SwitchModule, TenantMappingComponent],
  templateUrl: './configured-ingestion-form.component.html',
  styleUrls: ['./configured-ingestion-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfiguredIngestionFormComponent extends BaseComponent implements OnInit {
  @Input() loading = signal(false);
  @Input() integrationStatus?: string;
  @Input() integrationCategory?: string;
  organizationId = input<number | null>(null);
  integrationId = input<number | null>(null);
  savedConfiguration = signal<IngestorRecordDetail | null>(null);
  resyncLoading = signal(false);
  actionMessage = signal<string | null>(null);
  actionMessageType = signal<'success' | 'error'>('success');

  private ingestorService = inject(IngestorService);
  private connectWiseService = inject(ConnectWiseService);

  constructor() {
    super();
  }

  ngOnInit(): void {
    if (this.isStatusConfigured() && this.integrationId()) {
      if (this.integrationCategory === 'Ticketing') {
        this.loadTicketingConfiguration();
      } else {
        this.loadSavedConfiguration();
      }
    }
  }

  isStatusConfigured(): boolean {
    return (
      this.integrationStatus === 'Configured' ||
      this.integrationStatus === 'Connection Configured' ||
      this.integrationStatus === 'Configuration Configured'
    );
  }

  loadSavedConfiguration(): void {
    if (!this.organizationId()) {
      return;
    }

    this.loading.set(true);
    this.ingestorService
      .getIngestorRecordsByOrganizationv2(this.organizationId()!, this.integrationId()!).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        this.savedConfiguration.set(response ?? null);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.savedConfiguration.set(null);
      }
    });
  }

  elasticSyncStatus(): ElasticSyncStatus {
    return this.savedConfiguration()?.elastic_sync_status ?? null;
  }

  elasticSyncStatusLabel(): string | null {
    switch (this.elasticSyncStatus()) {
      case 'pending':
        return 'Elastic provisioning in progress';
      case 'active':
        return 'Elastic provisioning complete';
      case 'failed':
        return 'Elastic provisioning failed';
      case 'deleting':
        return 'Elastic cleanup in progress';
      default:
        return null;
    }
  }

  showElasticSyncWarning(): boolean {
    return ['pending', 'failed', 'deleting'].includes(this.elasticSyncStatus() ?? '');
  }

  canRetryElasticSync(): boolean {
    return this.elasticSyncStatus() === 'failed' && !!this.savedConfiguration()?.id;
  }

  retryElasticSync(): void {
    const recordId = this.savedConfiguration()?.id;
    if (!recordId) {
      return;
    }

    this.resyncLoading.set(true);
    this.actionMessage.set(null);
    this.ingestorService
      .resyncElastic(recordId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (record) => {
          this.savedConfiguration.set(record);
          this.resyncLoading.set(false);
          this.actionMessage.set('Elastic reprovisioning queued.');
          this.actionMessageType.set('success');
        },
        error: (error) => {
          this.resyncLoading.set(false);
          this.actionMessage.set(formatIngestorApiError(error, 'Failed to retry Elastic provisioning'));
          this.actionMessageType.set('error');
        },
      });
  }

  private loadTicketingConfiguration(): void {
    if (!this.organizationId()) {
      return;
    }

    this.loading.set(true);
    this.connectWiseService
      .getIntegrationDetails(this.organizationId()!, this.integrationId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.savedConfiguration.set({
            ingestor_integration: { name: response.integration_name || 'Ticketing Integration' },
            configuration_status: response.configuration_status,
            created_by: response.created_by || '',
            created_at: response.created_at || '',
            updated_at: response.updated_at || '',
          } as any);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.savedConfiguration.set(null);
        },
      });
  }
}
